import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { handleAPIError, TOAST_MESSAGES, showSuccessToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { analytics } from '@/services/analytics';
import {type ChatSession, isVideoChat,} from '@/models';
import { faSpinner, faChevronRight, faLock, faComments, faPlus } from '@/fontawesome';
import PageBasedPagination from '@/components/ui/PageBasedPagination';

const PAGE_SIZE = 50;

export default function ChatSessionsList() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingChat, setCreatingChat] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        const resp = await APIRoutes.listChatSessions({
          limit: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined,
          sort: 'updated',
          sortDirection: 'DESC',
          type: 'GENERIC' // Video-Chats und Video-Finder-Suchen gehören nicht in den Trading-Buddy
        });

        // Filter out deleted sessions AND video chat sessions (show only regular chats)
        setSessions(resp.sessions.filter(session =>
          session.deleted === null && !isVideoChat(session)
        ));
        setTotalSessions(resp.total);
      } catch (err) {
        setError('Fehler beim Laden der Chat-Sitzungen');
        handleAPIError(err, TOAST_MESSAGES.SESSIONS_LOAD_ERROR);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [currentPage]);

  const handleNewChat = async () => {
    try {
      setCreatingChat(true);
      const session = await APIRoutes.createChatSession({
        title: 'Neuer Chat',
        type: 'GENERIC'
      });
      analytics.track('Chat Started', {
        sessionId: session.id,
        type: 'GENERIC',
      });
      navigate(`/chats/${session.id}`);
      showSuccessToast(TOAST_MESSAGES.CHAT_CREATED);
    } catch (error) {
      handleAPIError(error, TOAST_MESSAGES.CHAT_CREATE_ERROR);
    } finally {
      setCreatingChat(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">
            Noch keine Chat-Sitzungen vorhanden
          </div>
          <p className="text-gray-400 dark:text-gray-500">
            Starten Sie einen neuen Chat, um Ihre Trading-Fragen zu stellen.
          </p>
        </div>

        {/* Sticky New Chat Button */}
        <button
          onClick={handleNewChat}
          disabled={creatingChat}
          className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer z-50 group"
          title="Neuer Chat"
        >
          <div className="flex items-center px-4 py-4">
            <FontAwesomeIcon
              icon={creatingChat ? faSpinner : faPlus}
              className={`w-6 h-6 flex-shrink-0 ${creatingChat ? 'animate-spin' : ''}`}
            />
            <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out">
              <span className="pl-3 whitespace-nowrap">Neuer Chat</span>
            </span>
          </div>
        </button>
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Meine Chat-Sitzungen
        </h2>

        <div className="grid gap-4">
          {sessions.map((session) => (
            <Link
              key={session.id}
              to={`/chats/${session.id}`}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 block"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-1">
                    <FontAwesomeIcon
                      icon={session.closed ? faLock : faComments}
                      className={`w-4 h-4 ${session.closed ? 'text-gray-500 dark:text-gray-400' : 'text-blue-600 dark:text-blue-400'}`}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-2 ${session.closed ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {session.title || 'Unbenannte Chat-Sitzung'}
                    </h3>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Erstellt: {formatDate(session.created)}
                    </div>
                    {session.closed && (
                      <div className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                        Archiviert
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                  <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <PageBasedPagination
          currentPage={currentPage}
          totalCount={totalSessions}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      </div>

      {/* Sticky New Chat Button */}
      <button
        onClick={handleNewChat}
        disabled={creatingChat}
        className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer z-50 group"
        title="Neuer Chat"
      >
        <div className="flex items-center px-4 py-4">
          <FontAwesomeIcon
            icon={creatingChat ? faSpinner : faPlus}
            className={`w-6 h-6 flex-shrink-0 ${creatingChat ? 'animate-spin' : ''}`}
          />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out">
            <span className="pl-3 whitespace-nowrap">Neuer Chat</span>
          </span>
        </div>
      </button>
    </>
  );
}
